import { Control, FieldValues, Path, useController } from 'react-hook-form'
import {
  type ComponentProps,
  type ComponentRef,
  forwardRef,
  type FunctionComponent,
  type Ref,
  useImperativeHandle,
  useRef,
} from 'react'
import { useEvent } from 'tamagui'
import { TextInput } from 'react-native'
import { Input } from '@/src/common/components/Input'
import { SardineId } from '@/src/services/Sardine/SardineId'

export interface FormInputProps<T extends FieldValues> extends ComponentProps<typeof Input> {
  control: Control<T>
  name: Path<T>
  sardineId?: SardineId
}

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const FormInput = forwardRef(function FormInput<T extends FieldValues>(
  { control, name, sardineId, onFocus, onBlur, ...props }: FormInputProps<T>,
  ref: Ref<TextInput | null>,
) {
  const { field, fieldState, formState } = useController({ control, name })
  const localRef = useRef<TextInput | null>(null)
  const info = props.info ?? {}

  // @ts-expect-error type differences between Ref.current and Ref
  // going to ignore for now
  useImperativeHandle(ref, () => localRef.current)
  useImperativeHandle(field.ref, () => localRef.current)

  const handleFocus: NonNullable<ComponentProps<typeof Input>['onFocus']> = useEvent((...args) => {
    onFocus?.(...args)
  })

  const handleBlur: NonNullable<ComponentProps<typeof Input>['onBlur']> = useEvent((...args) => {
    onBlur?.(...args)
    field.onBlur()
  })

  const handleChangeText: NonNullable<ComponentProps<typeof Input>['onChangeText']> = useEvent(
    (text) => {
      field.onChange(text)
    },
  )

  return (
    <Input
      {...props}
      sardineId={sardineId}
      value={field.value}
      onChangeText={handleChangeText}
      info={{
        ...info,
        error: formState.isValidating ? undefined : fieldState.error?.message,
      }}
      onBlur={handleBlur}
      onFocus={handleFocus}
      ref={localRef}
    />
  )
}) as FormInputType<ComponentRef<typeof Input>>

/** @see {@link https://github.com/Acre-Rewards/acre-app/pull/1896#discussion_r2274465439} */
export type FormInputType<R extends TextInput> = <T extends FieldValues>(
  props: FormInputProps<T> & { ref?: Ref<R | null> },
) => ReturnType<FunctionComponent<typeof props>>
